/**
 * Ending a session (ADR-0046, FR.2050, FR.2092).
 *
 * The browser cannot recall a bearer token it has handed out, so signing out
 * has two halves: tell the server to end the session record behind the token
 * (`POST /api/v1/auth/logout`), then make MSAL forget the account. After the
 * first half the server refuses that token everywhere, so a copy left behind
 * is useless.
 *
 * The server accepts a token up to 24 hours past its expiry for this one call,
 * because when a silent renewal fails the expired token is the only proof of
 * which session to end. The last token this tab used is therefore remembered
 * **in memory** (never in browser storage, CLAUDE.md §9.1) by `useAccessToken`.
 *
 * Reasons, as the server records them:
 * - `sign_out` — the person chose Sign out;
 * - `renewal_failed` — the directory refused a silent renewal (the account was
 *   disabled, the password reset, Conditional Access changed);
 * - `idle_timeout` — ten minutes without user activity (`IdleTimeoutProvider`).
 */

import type { IPublicClientApplication } from "@azure/msal-browser";
import { postToTabs } from "./tabs";

export type EndReason = "sign_out" | "renewal_failed" | "idle_timeout";

let lastToken: string | null = null;

/** Remember the latest access token this tab used (memory only). */
export function rememberToken(token: string): void {
  lastToken = token;
}

/** Tell the server to end the session behind `token`. Never throws: the
 *  session also ends at the idle limit, so a failed call is not fatal. */
export async function endServerSession(token: string | null, reason: EndReason): Promise<void> {
  if (!token) return;
  try {
    await fetch("/api/v1/auth/logout", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ reason }),
    });
  } catch {
    // Network failure: nothing more to do from here.
  }
}

/**
 * End the session for `reason`, then forget the account locally.
 *
 * `interactive` signs out through Microsoft's page as well (the explicit Sign
 * out); otherwise only MSAL's in-memory cache is cleared — the person is sent
 * to `/login`, which asks for a fresh sign-in (`prompt=login`).
 */
export async function endSession(
  instance: IPublicClientApplication,
  reason: EndReason,
  { interactive = false }: { interactive?: boolean } = {},
): Promise<void> {
  const token = lastToken;
  lastToken = null;
  await endServerSession(token, reason);
  // The person's other tabs share this session (ADR-0075): they go to
  // the login page now rather than at their next refused request.
  postToTabs({ type: "ended", reason });
  if (interactive) {
    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    try {
      await instance.logoutPopup({ account });
      return;
    } catch {
      // A blocked popup must not leave the account signed in here.
    }
  }
  await instance.clearCache();
}

/** Where to send someone whose session ended without them choosing it. */
export function loginPathFor(reason: "session_ended" | EndReason): string {
  return `/login?reason=${encodeURIComponent(reason)}`;
}

/** Test hook. */
export function _resetSessionForTests(): void {
  lastToken = null;
}
